// Monte Carlo engine — implements simulation logic per spec §6 + v2 extensions
// (bootstrap mode, commissions, behavioral model, instrument-aware MAE, attempt curve)

// ─── Instrument MAE ratios (empirical) ─────────────────────────────────────
export const INSTRUMENT_MAE_RATIOS = {
  nq:      { winScale: 0.91, lossRatio: 2.1, label: "NQ futures" },
  es:      { winScale: 0.75, lossRatio: 1.8, label: "ES futures" },
  cl:      { winScale: 1.10, lossRatio: 2.4, label: "CL futures" },
  gc:      { winScale: 0.85, lossRatio: 2.0, label: "GC futures" },
  eurusd:  { winScale: 0.55, lossRatio: 1.5, label: "EUR/USD forex" },
  gbpusd:  { winScale: 0.60, lossRatio: 1.6, label: "GBP/USD forex" },
  custom:  { winScale: 0.80, lossRatio: 2.0, label: "Custom / Unknown" },
};

// ─── Primitives ────────────────────────────────────────────────────────────
// ─── PRNG (Fase 14 · Bloque 1) ────────────────────────────────────────────
// All sampling MUST go through `_rand()` so the engine is reproducible via
// `opts.seed`. A module-level variable is safe because we never run more than
// one simulation concurrently in the same module (the Web Worker — Bloque 2 —
// imports the module separately, so it has its own _rand).
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
let _rand = Math.random;

function normalRandom(mu, sigma) {
  let u1;
  do { u1 = _rand(); } while (u1 === 0);
  return mu + sigma * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * _rand());
}
function gammaRandom(shape, scale) {
  if (shape < 1) return gammaRandom(1 + shape, scale) * Math.pow(_rand(), 1 / shape);
  const d = shape - 1 / 3, c = 1 / Math.sqrt(9 * d);
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const x = normalRandom(0, 1), v0 = 1 + c * x;
    if (v0 <= 0) continue;
    const v = v0 * v0 * v0, u = _rand();
    if (u < 1 - 0.0331 * x * x * x * x) return d * v * scale;
    if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) return d * v * scale;
  }
}
function quantile(sortedArr, p) {
  if (!sortedArr.length) return 0;
  return sortedArr[Math.min(Math.floor(sortedArr.length * p), sortedArr.length - 1)];
}
function mean(arr) { return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0; }

function computeFloor(account, hwm, equityEOD, phaseCapital) {
  if (account.ddType === "static") return phaseCapital - account.ddValue;
  const raw = hwm - account.ddValue;
  switch (account.floorLock) {
    case "none":                 return raw;
    case "at_capital":           return Math.min(raw, phaseCapital);
    case "at_target_level":      return Math.min(raw, phaseCapital + account.target);
    case "at_capital_plus_100":
      if (equityEOD >= phaseCapital + account.ddValue + 100) return phaseCapital + 100;
      return raw;
    default:                     return Math.min(raw, phaseCapital);
  }
}

// ─── Daily pnl/mae generator ───────────────────────────────────────────────
// Returns { pnl, mae } — accounts for Simple vs Bootstrap, commissions, MAE mode.
// `sampler` (Fase 14 · Bloque 3) carries per-trajectory state for the
// stationary (block) bootstrap: a cursor that advances through the dataset
// and, with probability 1/L per step, jumps to a fresh random index.
function genDay(strategy, sizeFactor, sampler) {
  let pnl;
  let bootMae;
  if (strategy.mode === "bootstrap" && strategy.bootstrapData && strategy.bootstrapData.length > 0) {
    const n = strategy.bootstrapData.length;
    const L = strategy.blockSampling === false
      ? 1
      : (strategy.bootstrapStats?.blockLen || 1);
    let idx;
    if (!sampler || sampler.idx < 0 || L === 1 || _rand() < 1 / L) {
      idx = Math.floor(_rand() * n);
    } else {
      idx = (sampler.idx + 1) % n;
    }
    if (sampler) sampler.idx = idx;
    const d = strategy.bootstrapData[idx];
    pnl = d.pnl;
    if (d.mae !== undefined && d.mae !== null) bootMae = d.mae;
  } else {
    const isWin = _rand() < strategy.wr;
    pnl = isWin
      ? normalRandom(strategy.muWin,  strategy.sigmaWin)
      : normalRandom(strategy.muLoss, strategy.sigmaLoss);
    if (_rand() < strategy.tailProb) pnl *= strategy.tailMult;
  }

  // ─── Stress test injection (Fase 13) ─────────────────────────────────────
  // Applied BEFORE sizeFactor multiplication so the effective WR shift is
  // semantically clean (we swap a winning day for a real losing day) and the
  // loss-amplification factor multiplies the raw PnL, not the scaled one.
  // Business sim (Sep 2026) — "what if my edge is X% smaller?": shifts every
  // day down by a fixed fraction of the historical mean daily pnl.
  if (strategy._edgeShift) pnl -= strategy._edgeShift;

  const st = strategy.stress;
  if (st) {
    if (st.winErosionQ > 0 && pnl > 0 && _rand() < st.winErosionQ) {
      if (strategy.mode === "bootstrap" && strategy._lossPool && strategy._lossPool.length) {
        const ld = strategy._lossPool[Math.floor(_rand() * strategy._lossPool.length)];
        pnl = ld.pnl;
        if (ld.mae !== undefined && ld.mae !== null) bootMae = ld.mae;
        else bootMae = undefined; // fall back to estimator for this swapped day
      } else if (strategy.mode !== "bootstrap") {
        pnl = normalRandom(strategy.muLoss, strategy.sigmaLoss);
      }
    }
    if (st.lossAmplify > 0 && pnl < 0) {
      pnl *= (1 + st.lossAmplify);
    }
  }

  // Fase 14 · Bloque 5 — global size multiplier (sizing sweep)
  // sizeFactor is the per-trajectory adjustment (post-target / funded reduction);
  // sizeMultiplier is the strategy-level "what if I traded N× my historical size?".
  const gMul = sizeFactor * (strategy.sizeMultiplier || 1);
  pnl *= gMul;

  // MAE resolution
  let mae;
  if (bootMae !== undefined) {
    mae = bootMae * gMul;
  } else if (strategy.maeMode === "manual") {
    const baseMae = pnl > 0 ? (strategy.maeWinMean ?? 0) : (strategy.maeLossMean ?? 0);
    const stdMae  = pnl > 0 ? (strategy.maeWinStd  ?? 0) : (strategy.maeLossStd  ?? 0);
    mae = Math.max(0, normalRandom(baseMae, stdMae)) * gMul;
    if (pnl < 0) mae = Math.max(mae, Math.abs(pnl));
  } else {
    // ESTIMATE (default) — instrument ratios, else legacy maeWin/maeLoss scaling
    const inst = INSTRUMENT_MAE_RATIOS[strategy.instrument] || INSTRUMENT_MAE_RATIOS.custom;
    const winScale  = inst.winScale;
    const lossRatio = inst.lossRatio;
    // Use sigmas from the bootstrapped data when available — falling back to the
    // Simple-mode sigmas otherwise. This keeps the MAE scale coherent with the
    // user's actual PnL distribution.
    const bsStats = strategy.mode === "bootstrap" ? strategy.bootstrapStats : null;
    const srcSigmaWin  = (bsStats && bsStats.sigmaWin)  ? bsStats.sigmaWin  : strategy.sigmaWin;
    const srcSigmaLoss = (bsStats && bsStats.sigmaLoss) ? bsStats.sigmaLoss : strategy.sigmaLoss;
    const sigW = Math.max(srcSigmaWin  * winScale, 1);
    const sigL = Math.max(srcSigmaLoss * 0.3, 1);
    mae = (pnl > 0
      ? gammaRandom(2.0, sigW)
      : Math.abs(pnl) * lossRatio + gammaRandom(1.5, sigL)) * (strategy.sizeMultiplier || 1);
  }

  // Amplify MAE on losing days under stress (worse stop execution).
  // Applied AFTER MAE resolution so it works across all three MAE modes.
  if (st && st.lossAmplify > 0 && pnl < 0) {
    mae *= (1 + st.lossAmplify);
  }

  // Subtract commissions (applied to net pnl, not MAE)
  const comm = strategy.dailyCommission || 0;
  pnl -= comm * gMul;

  return { pnl, mae };
}

// ─── Single phase simulation ───────────────────────────────────────────────
function simulateSinglePhase(account, strategy, phaseCapital, behavioral) {
  let equity      = phaseCapital;
  let hwm         = phaseCapital;
  let floor       = phaseCapital - account.ddValue;
  let totalProfit = 0;
  let bestDay     = 0;
  let dias        = 0;
  let winningDays = 0;
  let calendarDays = 0;
  let floorLocked = false;
  let sizeFactor  = 1.0;
  const TOPE      = 5000;
  const sampler   = { idx: -1 }; // Fase 14 · Bloque 3 — block-bootstrap cursor

  const b = behavioral || {};
  const postConservative  = b.postTargetMode === "conservative";
  const reductionFactor   = b.postTargetSizeReduction ?? 0.30;
  const minDaysType       = b.minDaysType || "total";
  const winDayThreshold   = b.winDayThreshold ?? 50;
  const maxDaysType       = b.maxDaysType || "trading";

  while (dias < TOPE) {
    dias++;
    if (maxDaysType === "calendar") {
      calendarDays = Math.ceil(dias * 1.4);
    }

    const activeMaxDays = maxDaysType === "calendar" ? calendarDays : dias;
    if (account.maxDays && activeMaxDays > account.maxDays) return { result: "TIMEOUT", dias };

    const { pnl: pnlRaw, mae } = genDay(strategy, sizeFactor, sampler);
    let pnl = pnlRaw;

    // MFE raises floor mid-day for intraday trailing.
    // Fase 14 · Bloque 1c (V4 fix): a losing day can still print a positive
    // intraday peak (a morning rally) which raises the trailing floor.
    if (account.ddType === "trailing_intraday" && !floorLocked) {
      const bsStats = strategy.mode === "bootstrap" ? strategy.bootstrapStats : null;
      const sW = (bsStats && bsStats.sigmaWin) ? bsStats.sigmaWin : strategy.sigmaWin;
      const mfe = pnl > 0
        ? pnl + gammaRandom(1.5, Math.max(sW * 0.15, 1))
        : gammaRandom(1.2, Math.max(sW * 0.35, 1)); // losing-day peak
      const peakEquity = equity + mfe;
      if (peakEquity > hwm) {
        hwm = peakEquity;
        const newFloor = computeFloor(account, hwm, equity, phaseCapital);
        floor = Math.max(floor, newFloor);
      }
    }

    // Intraday MAE check
    if (equity - mae <= floor) return { result: "DD", dias };

    // DLL
    if (account.dailyLoss && pnl <= -account.dailyLoss) {
      if (account.dailyLossIsFatal) return { result: "DLL", dias };
      pnl = -account.dailyLoss;
    }

    equity += pnl;
    if (equity <= floor) return { result: "DD", dias };

    if (account.ddType !== "static" && !floorLocked) {
      if (equity > hwm) hwm = equity;
      if (account.floorLock === "at_capital_plus_100" &&
          equity >= phaseCapital + account.ddValue + 100) {
        floorLocked = true;
        floor = phaseCapital + 100;
      } else {
        const newFloor = computeFloor(account, hwm, equity, phaseCapital);
        floor = Math.max(floor, newFloor);
      }
    }

    if (pnl > 0) {
      totalProfit += pnl;
      if (pnl > bestDay) bestDay = pnl;
    }
    if (pnl >= winDayThreshold) winningDays++;

    let effectiveTarget = account.target;
    let consistencyOK   = true;
    if (account.consistency && account.consistencyType === "vs_target" && bestDay > 0) {
      effectiveTarget = Math.max(account.target, bestDay / account.consistency);
    } else if (account.consistency && account.consistencyType === "vs_total") {
      if (totalProfit > 0) consistencyOK = bestDay <= account.consistency * totalProfit;
    }

    const minDaysMet = minDaysType === "winning"
      ? winningDays >= (account.minDays || 0)
      : dias >= (account.minDays || 0);

    const profitMade = equity - phaseCapital;
    const targetReached = profitMade >= effectiveTarget;

    if (targetReached && minDaysMet && consistencyOK) {
      return { result: "PASS", dias };
    }
    if (targetReached && !consistencyOK && postConservative) {
      // Reduce size to dilute best-day ratio without taking excessive risk
      sizeFactor = reductionFactor;
    } else if (!targetReached && sizeFactor < 1.0 && postConservative) {
      // If we've drifted back below target (shouldn't normally happen since we only reduced after target hit),
      // resume normal size
      sizeFactor = 1.0;
    }
  }
  return { result: "DD", dias };
}

function simulateAttempt(account, strategy, behavioral) {
  const r1 = simulateSinglePhase(account, strategy, account.capital, behavioral);
  if (r1.result !== "PASS") {
    return { result: r1.result, dias: r1.dias, passedPhase1: false };
  }
  if (!account.phases || account.phases === 1) {
    return { result: "PASS", dias: r1.dias, passedPhase1: true };
  }
  const phase2Account = {
    ...account,
    target:  account.phase2.target,
    minDays: account.phase2.minDays ?? 0,
    maxDays: account.phase2.maxDays ?? null,
  };
  const r2 = simulateSinglePhase(phase2Account, strategy, account.capital, behavioral);
  return {
    result:  r2.result,
    dias:    r1.dias + r2.dias,
    passedPhase1: true,
    phase1Days: r1.dias,
    phase2Days: r2.dias,
  };
}

function makeHistogram(data, bins = 28) {
  if (!data.length) return [];
  const sorted = [...data].sort((a, b) => a - b);
  const p95v = quantile(sorted, 0.95);
  if (p95v === 0) return [];
  const clipped = data.filter(d => d <= p95v);
  const bSz = Math.max(1, Math.ceil(p95v / bins));
  const map = new Map();
  clipped.forEach(d => {
    const b = Math.floor(d / bSz) * bSz;
    map.set(b, (map.get(b) || 0) + 1);
  });
  return [...map.entries()].sort((a, b) => a[0] - b[0])
    .map(([day, cnt]) => ({ day, pct: +((cnt / clipped.length) * 100).toFixed(2) }));
}

// ─── Payout decision (shared by single-account and business sims) ─────────
// `st` is the funded account's running state since its last payout:
//   { equity, nPayouts, daysSince, winDaysSince, bestDaySince, profitSince, netSince }
// Returns { gross, net, fee } when a payout is allowed today, else null.
// Rules added in the Sep 2026 audit are all no-ops at their defaults, so
// firms that don't declare them behave exactly as before.
function payoutDecision(st, rules, capital, split) {
  const minDaysMet = st.nPayouts === 0
    ? st.daysSince >= (rules.firstPayoutMinDays || 0)
    : st.daysSince >= (rules.payoutFrequency   || 0);
  if (!minDaysMet) return null;
  if (rules.payoutWinDays > 0 && st.winDaysSince < rules.payoutWinDays) return null;

  // Safety net (Apex threshold, TakeProfit buffer, Tradeify Daily buffer):
  // the balance must stay above capital + net after the withdrawal.
  let buffer = rules.payoutBuffer || 0;
  const netActive = rules.safetyNet > 0 &&
    (rules.safetyNetPayouts == null || st.nPayouts < rules.safetyNetPayouts);
  if (netActive) buffer = Math.max(buffer, rules.safetyNet);

  const minAmount = rules.payoutMinAmount || 0;
  const profitAboveBuffer = st.equity - capital - buffer;
  if (profitAboveBuffer < minAmount || profitAboveBuffer <= 0) return null;

  if (rules.payoutConsistency && rules.payoutConsistencyType === "vs_total") {
    if (st.profitSince > 0 && st.bestDaySince > rules.payoutConsistency * st.profitSince) return null;
  }

  let gross = profitAboveBuffer * (rules.payoutMaxPct ?? 1.0);
  const sched = rules.payoutCapSchedule;
  const cap = sched && sched.length
    ? sched[Math.min(st.nPayouts, sched.length - 1)]
    : rules.payoutMaxCap;
  if (cap && gross > cap) gross = cap;
  if (rules.payoutMaxFreshMult) gross = Math.min(gross, rules.payoutMaxFreshMult * Math.max(0, st.netSince));
  if (gross < minAmount || gross <= 0) return null;

  const fee = rules.payoutFee && (rules.payoutFeeMaxGross == null || gross <= rules.payoutFeeMaxGross)
    ? rules.payoutFee : 0;
  return { gross, fee, net: gross * split - fee };
}

// ─── Funded phase simulation ───────────────────────────────────────────────
// Returns: { days, breach, graduated, payouts: [{day, gross, net}], payoutCount, totalGross, totalNet, finalEquity }
function simulateFundedPhase(plan, strategy, fundedRules, horizonDays, sizeFactorFunded) {
  const capital = plan.capital;
  const ddType  = fundedRules.ddType;
  const ddValue = fundedRules.ddValue;
  const dailyLoss = fundedRules.dailyLoss;
  const dailyLossIsFatal = fundedRules.dailyLossIsFatal;
  const floorLockType = fundedRules.floorLock;
  const split = plan.profitSplit;

  let equity = capital;
  let hwm    = capital;
  let floor  = capital - ddValue;
  let floorLocked = false;
  let daysSinceLastPayout = 0;
  let dayIdx = 0;
  let bestDaySincePayout = 0;
  let profitSincePayout = 0;
  let winDaysSincePayout = 0;
  let equityAtLastPayout = capital;
  const payouts = [];

  // Synthetic "account" shape for floor recomputation helpers
  const acct = { ddType, ddValue, floorLock: floorLockType, target: 0 };
  const sampler = { idx: -1 }; // Fase 14 · Bloque 3

  while (dayIdx < horizonDays) {
    dayIdx++;
    daysSinceLastPayout++;

    const { pnl: pnlRaw, mae } = genDay(strategy, sizeFactorFunded, sampler);
    let pnl = pnlRaw;

    // Intraday trailing: MFE raises floor mid-day (V4 — also on losing days)
    if (ddType === "trailing_intraday" && !floorLocked) {
      const bsStats = strategy.mode === "bootstrap" ? strategy.bootstrapStats : null;
      const sW = (bsStats && bsStats.sigmaWin) ? bsStats.sigmaWin : strategy.sigmaWin;
      const mfe = pnl > 0
        ? pnl + gammaRandom(1.5, Math.max(sW * 0.15, 1))
        : gammaRandom(1.2, Math.max(sW * 0.35, 1));
      const peakEquity = equity + mfe;
      if (peakEquity > hwm) {
        hwm = peakEquity;
        const nf = computeFloor(acct, hwm, equity, capital);
        floor = Math.max(floor, nf);
      }
    }

    // Intraday MAE breach
    if (equity - mae <= floor) {
      return { days: dayIdx, breach: "DD", payouts, payoutCount: payouts.length,
               totalGross: sum(payouts.map(p => p.gross)),
               totalNet:   sum(payouts.map(p => p.net)),
               finalEquity: equity };
    }

    // Daily loss
    if (dailyLoss && pnl <= -dailyLoss) {
      if (dailyLossIsFatal) {
        return { days: dayIdx, breach: "DLL", payouts, payoutCount: payouts.length,
                 totalGross: sum(payouts.map(p => p.gross)),
                 totalNet:   sum(payouts.map(p => p.net)),
                 finalEquity: equity };
      }
      pnl = -dailyLoss;
    }

    equity += pnl;
    if (equity <= floor) {
      return { days: dayIdx, breach: "DD", payouts, payoutCount: payouts.length,
               totalGross: sum(payouts.map(p => p.gross)),
               totalNet:   sum(payouts.map(p => p.net)),
               finalEquity: equity };
    }

    if (ddType !== "static" && !floorLocked) {
      if (equity > hwm) hwm = equity;
      if (floorLockType === "at_capital_plus_100" && equity >= capital + ddValue + 100) {
        floorLocked = true;
        floor = capital + 100;
      } else {
        const nf = computeFloor(acct, hwm, equity, capital);
        floor = Math.max(floor, nf);
      }
    }

    if (pnl > 0) {
      profitSincePayout += pnl;
      if (pnl > bestDaySincePayout) bestDaySincePayout = pnl;
    }
    if (fundedRules.payoutWinDays > 0 && pnl >= (fundedRules.payoutWinDayMin || 0) && pnl > 0) {
      winDaysSincePayout++;
    }

    // ─── Payout eligibility check ───
    const decision = payoutDecision({
      equity, nPayouts: payouts.length, daysSince: daysSinceLastPayout,
      winDaysSince: winDaysSincePayout, bestDaySince: bestDaySincePayout,
      profitSince: profitSincePayout, netSince: equity - equityAtLastPayout,
    }, fundedRules, capital, split);
    if (!decision) continue;

    const { gross, net } = decision;
    payouts.push({ day: dayIdx, gross, net });

    // Post-payout balance / floor behavior
    if (fundedRules.resetOnPayout) {
      equity -= gross;
      if (fundedRules.resetFloorOnPayout !== false) {
        hwm = capital;
        floor = capital - ddValue;
        floorLocked = false;
      }
    } else {
      equity -= gross;
      // Most non-resetting firms keep trailing floor tied to hwm; do not lower hwm
    }
    daysSinceLastPayout = 0;
    bestDaySincePayout  = 0;
    profitSincePayout   = 0;
    winDaysSincePayout  = 0;
    equityAtLastPayout  = equity;

    // Graduation (Apex 6 cycles → live, Tradeify 5 → Elite, Lucid 4): the
    // funded account stops being simulated. Counted as "no breach".
    if (fundedRules.maxPayouts && payouts.length >= fundedRules.maxPayouts) {
      return { days: dayIdx, breach: null, graduated: true, payouts, payoutCount: payouts.length,
               totalGross: sum(payouts.map(p => p.gross)),
               totalNet:   sum(payouts.map(p => p.net)),
               finalEquity: equity };
    }
  }

  return { days: dayIdx, breach: null, payouts, payoutCount: payouts.length,
           totalGross: sum(payouts.map(p => p.gross)),
           totalNet:   sum(payouts.map(p => p.net)),
           finalEquity: equity };
}
function sum(a) { return a.reduce((x, y) => x + y, 0); }

// ─── Input validation (Fase 14 · Bloque 1b) ───────────────────────────────
// Throws on impossible configurations (capital/target/dd <= 0); clamps WR,
// sigmas, fee and nSims so they cannot produce NaN downstream.
function validateInputs(account, strategy, nSims) {
  if (!(account?.capital > 0)) throw new Error("invalid account: capital must be > 0");
  if (!(account?.target  > 0)) throw new Error("invalid account: target must be > 0");
  if (!(account?.ddValue > 0)) throw new Error("invalid account: ddValue must be > 0");
  const s = { ...strategy };
  s.wr = Math.min(1, Math.max(0, s.wr ?? 0));
  for (const k of ["sigmaWin", "sigmaLoss", "tailProb"]) {
    if (s[k] < 0) s[k] = 0;
  }
  let a = account;
  if (a.fee < 0) a = { ...a, fee: 0 };
  const N = Math.min(25000, Math.max(100, Math.floor(nSims || 10000)));
  return { account: a, strategy: s, nSims: N };
}

export function runMonteCarlo(account, strategy, nSims, fundedRules, opts = {}) {
  // ─── Fase 14 · Bloque 1a/1b: validate + seed RNG ────────────────────────
  const v = validateInputs(account, strategy, nSims);
  account = v.account; strategy = v.strategy;
  const N = v.nSims;
  const seed = Number.isFinite(opts.seed)
    ? (opts.seed >>> 0)
    : ((Date.now() ^ (Math.random() * 1e9)) >>> 0);
  _rand = mulberry32(seed);

  const diasPass = [], diasFail = [];
  let nPass = 0, nDD = 0, nTimeout = 0, nDLL = 0;

  // Post-PASS aggregators
  const postPassEnabled = !!strategy.postPassEnabled && !!fundedRules;
  const horizonMonths   = Math.max(1, Math.min(24, strategy.postPassHorizonMonths || 6));
  const horizonDays     = Math.round(horizonMonths * 21);
  const postSizeFactor  = strategy.postPassSizeMode === "reduced"
    ? Math.max(0.01, Math.min(1, strategy.postPassSizeFactor ?? 0.5))
    : 1.0;
  const ppNetAll = [];
  const ppGrossAll = [];
  const ppPayoutsAll = [];
  const ppDaysSurvived = [];
  const ppFirstPayoutDay = [];
  const ppBreachCount = { DD: 0, DLL: 0, None: 0 };
  let   ppGraduated = 0; // reached the firm's max payout cycles (live / Elite)
  const ppSurvive3m = []; const ppSurvive6m = []; const ppSurvive12m = [];
  const ppPayoutsByMonth = Array.from({ length: horizonMonths }, () => 0); // mean net payout per month
  let   ppRuns = 0;

  // Derive daily commission from mode + params
  const resolved = { ...strategy };
  if (strategy.commissionMode === "estimate") {
    resolved.dailyCommission =
      (strategy.commissionPerRT || 0) *
      (strategy.tradesPerDay || 0) *
      (strategy.contractsPerTrade || 1);
  } else if (strategy.commissionMode === "fixed") {
    resolved.dailyCommission = strategy.dailyCommission || 0;
  } else {
    resolved.dailyCommission = 0;
  }

  const behavioral = {
    postTargetMode:         strategy.postTargetMode || "conservative",
    postTargetSizeReduction: strategy.postTargetSizeReduction ?? 0.30,
    minDaysType:            strategy.minDaysType || "total",
    winDayThreshold:        strategy.winDayThreshold ?? 50,
    maxDaysType:            strategy.maxDaysType || "trading",
  };

  function baseFeeForAttempt(dias) {
    if (!account.feeType || account.feeType === "one_time") return account.fee;
    return account.fee * Math.ceil(Math.max(dias, 1) / 21);
  }

  let totalCost = 0;

  for (let i = 0; i < N; i++) {
    const r = simulateAttempt(account, resolved, behavioral);
    const base = baseFeeForAttempt(r.dias);
    const activation = r.result === "PASS" ? (account.activationFee || 0) : 0;
    totalCost += base + activation;

    if      (r.result === "PASS")    { nPass++;    diasPass.push(r.dias); }
    else if (r.result === "TIMEOUT") { nTimeout++; diasFail.push(r.dias); }
    else if (r.result === "DLL")     { nDLL++;     diasFail.push(r.dias); }
    else                             { nDD++;      diasFail.push(r.dias); }

    // ── Post-PASS funded cycle ──
    if (postPassEnabled && r.result === "PASS") {
      const fr = simulateFundedPhase(account, resolved, fundedRules, horizonDays, postSizeFactor);
      ppRuns++;
      ppNetAll.push(fr.totalNet);
      ppGrossAll.push(fr.totalGross);
      ppPayoutsAll.push(fr.payoutCount);
      ppDaysSurvived.push(fr.days);
      ppFirstPayoutDay.push(fr.payouts.length ? fr.payouts[0].day : null);
      if (fr.breach === "DD")       ppBreachCount.DD++;
      else if (fr.breach === "DLL") ppBreachCount.DLL++;
      else                          ppBreachCount.None++;
      if (fr.graduated) ppGraduated++;

      ppSurvive3m.push(fr.days >= Math.min(horizonDays,  63) && !fr.breach ? 1 : (fr.days >=  63 ? 1 : 0));
      ppSurvive6m.push(fr.days >= Math.min(horizonDays, 126) && !fr.breach ? 1 : (fr.days >= 126 ? 1 : 0));
      ppSurvive12m.push(fr.days >= Math.min(horizonDays,252) && !fr.breach ? 1 : (fr.days >= 252 ? 1 : 0));

      for (const p of fr.payouts) {
        const m = Math.min(horizonMonths - 1, Math.floor((p.day - 1) / 21));
        if (m >= 0 && m < horizonMonths) ppPayoutsByMonth[m] += p.net;
      }
    }
  }

  const pPass    = nPass / N;
  const pDD      = nDD / N;
  const pTimeout = nTimeout / N;
  const pDLL     = nDLL / N;

  const sortedPass = [...diasPass].sort((a, b) => a - b);
  const sortedFail = [...diasFail].sort((a, b) => a - b);
  const avgCost    = totalCost / N;

  const finalTarget = account.phases === 2 ? account.phase2.target : account.target;
  const payout = finalTarget * account.profitSplit;

  const ev = pPass * payout - avgCost;
  const ruinaMin = payout > 0 ? avgCost / payout : 0;

  const pSafe = Math.max(pPass, 1e-9);
  const logFail = Math.log(1 - pSafe);
  const n95 = logFail < 0 ? Math.ceil(Math.log(0.05) / logFail) : Infinity;
  const n99 = logFail < 0 ? Math.ceil(Math.log(0.01) / logFail) : Infinity;
  const medIntentos = logFail < 0 ? Math.max(1, Math.ceil(Math.log(0.5) / logFail)) : Infinity;
  const passEssentiallyZero = pPass < 0.0001;

  const br95 = isFinite(n95) ? Math.round(n95 * avgCost) : Infinity;
  const br99 = isFinite(n99) ? Math.round(n99 * avgCost) : Infinity;

  // Attempt curve: probability of at least one pass vs number of attempts
  const attemptCurve = [];
  if (isFinite(logFail) && logFail < 0) {
    const milestones = [1, 2, 3, 5, 7, 10, 15, 20, 25, 30, 40, 50];
    for (const k of milestones) {
      const pAtLeastOne = 1 - Math.pow(1 - pPass, k);
      attemptCurve.push({
        attempts: k,
        pAtLeastOne: +(pAtLeastOne * 100).toFixed(2),
        bankroll: Math.round(k * avgCost),
      });
    }
  }

  // Commission impact breakdown
  const avgDays = diasPass.length ? mean(diasPass) : mean([...diasPass, ...diasFail]);
  const commissionImpact = {
    daily:    resolved.dailyCommission || 0,
    perAttempt: (resolved.dailyCommission || 0) * avgDays,
    avgDays,
  };

  // ─── Post-PASS (funded) aggregates ───
  let postPass = null;
  if (postPassEnabled && ppRuns > 0) {
    const sortedNet = [...ppNetAll].sort((a, b) => a - b);
    const sortedGross = [...ppGrossAll].sort((a, b) => a - b);
    const sortedFirst = ppFirstPayoutDay.filter(d => d !== null).sort((a, b) => a - b);
    const sortedPayouts = [...ppPayoutsAll].sort((a, b) => a - b);

    // Conditional: among PASSers
    // FIX 3a — only report survival windows that fit inside the simulated horizon.
    const pSurvive3m  = horizonDays >=  63 ? (ppSurvive3m.length  ? mean(ppSurvive3m)  : 0) : null;
    const pSurvive6m  = horizonDays >= 126 ? (ppSurvive6m.length  ? mean(ppSurvive6m)  : 0) : null;
    const pSurvive12m = horizonDays >= 252 ? (ppSurvive12m.length ? mean(ppSurvive12m) : 0) : null;

    const meanNet     = mean(ppNetAll);
    const meanGross   = mean(ppGrossAll);
    const meanPayouts = mean(ppPayoutsAll);
    const medianNet   = quantile(sortedNet, 0.5);
    const p10Net      = quantile(sortedNet, 0.10);
    const p90Net      = quantile(sortedNet, 0.90);
    const meanFirstPayoutDay = sortedFirst.length ? mean(sortedFirst) : null;
    const medFirstPayoutDay  = sortedFirst.length ? quantile(sortedFirst, 0.5) : null;

    // Net histogram (for distribution panel)
    const histNet = makeNetHistogram(ppNetAll);
    const payoutHistogram = makePayoutCountHistogram(ppPayoutsAll);

    // Unconditional expected take-home (weighted by P(PASS) so we can factor cost/ROI end-to-end)
    const evLifetime = pPass * meanNet - avgCost;

    // Fase 14 · Bloque 1d: probability of completing at least one payout
    // before breach or horizon end (conditional on PASS).
    const pAtLeastOnePayout = ppPayoutsAll.length
      ? ppPayoutsAll.filter(c => c >= 1).length / ppPayoutsAll.length
      : 0;

    // Breach breakdown (normalize to ppRuns)
    const breachPct = {
      DD:   ppBreachCount.DD   / ppRuns,
      DLL:  ppBreachCount.DLL  / ppRuns,
      None: ppBreachCount.None / ppRuns,
    };

    // Mean net payout per month (averaged across PASS runs)
    const payoutsByMonth = ppPayoutsByMonth.map((total, i) => ({
      month: i + 1,
      meanNet: +(total / ppRuns).toFixed(2),
    }));

    postPass = {
      ppRuns,
      horizonDays, horizonMonths,
      sizeFactor: postSizeFactor,
      pSurvive3m, pSurvive6m, pSurvive12m,
      meanNet, medianNet, p10Net, p90Net,
      meanGross,
      meanPayouts,
      medianPayouts: quantile(sortedPayouts, 0.5),
      meanFirstPayoutDay, medFirstPayoutDay,
      evLifetime,
      pAtLeastOnePayout,
      breachPct,
      graduatedPct: ppGraduated / ppRuns,
      histNet,
      payoutHistogram,
      payoutsByMonth,
      meanDaysSurvived: mean(ppDaysSurvived),
    };
  }

  return {
    pPass, pDD, pTimeout, pDLL, ruinaMin,
    meanPass: mean(diasPass), medianPass: quantile(sortedPass, 0.5), p90Pass: quantile(sortedPass, 0.9),
    meanFail: mean(diasFail), medianFail: quantile(sortedFail, 0.5),
    ev, avgCost, payout, finalTarget,
    n95, n99, br95, br99, medIntentos,
    passEssentiallyZero,
    histPass: makeHistogram(diasPass),
    histFail: makeHistogram(diasFail),
    attemptCurve,
    commissionImpact,
    postPass,
    mode: strategy.mode || "simple",
    nPass, nDD, nTimeout, nDLL,
    nSims: N,
    seed,
  };
}

// Net-payout histogram (clipped at 95th percentile)
function makeNetHistogram(data, bins = 24) {
  if (!data.length) return [];
  const sorted = [...data].sort((a, b) => a - b);
  const p95v = quantile(sorted, 0.95);
  const p05v = quantile(sorted, 0.05);
  if (p95v === p05v) return [];
  const range = p95v - p05v;
  const bSz = Math.max(1, Math.ceil(range / bins));
  const clipped = data.filter(d => d >= p05v && d <= p95v);
  const map = new Map();
  clipped.forEach(d => {
    const b = Math.floor(d / bSz) * bSz;
    map.set(b, (map.get(b) || 0) + 1);
  });
  return [...map.entries()].sort((a, b) => a[0] - b[0])
    .map(([bucket, cnt]) => ({ bucket, pct: +((cnt / clipped.length) * 100).toFixed(2) }));
}

function makePayoutCountHistogram(data) {
  if (!data.length) return [];
  const map = new Map();
  data.forEach(v => map.set(v, (map.get(v) || 0) + 1));
  return [...map.entries()].sort((a, b) => a[0] - b[0])
    .map(([count, freq]) => ({ count, pct: +((freq / data.length) * 100).toFixed(2) }));
}

// ─── Bootstrap data parser ─────────────────────────────────────────────────
// Accepts textarea content; returns { data: [{pnl, mae?, mfe?}], stats: {...}, errors: [] }
export function parseBootstrapData(raw) {
  if (!raw || typeof raw !== "string") return { data: [], stats: null, errors: ["Empty input"] };
  let lines = raw.trim().split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return { data: [], stats: null, errors: ["No lines found"] };

  // ─── Decimal-comma normalization (European number formats) ────────────────
  // Excel ES/LATAM exports numbers like `1.234,56` (dot=thousands, comma=decimal).
  // The naive "split by comma" parser would mis-read these as multiple columns.
  // We handle two cases:
  //   A) No tab/semicolon delimiter, every data line matches a single
  //      "european number" pattern → it's a single column. Normalise.
  //   B) Delimiter is tab or ';' → numbers may still be european inside each
  //      token. Normalise token-by-token before parseFloat.
  const EU_NUM_RE   = /^-?\d{1,3}(?:\.\d{3})+,\d{1,2}$|^-?\d+,\d{1,2}$/;
  const SINGLE_NUM_RE = /^-?\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?$|^-?\d+(?:,\d{1,2})?$/;
  const normalizeEU = (tok) => tok.replace(/\./g, "").replace(",", ".");

  const hasTab = lines.some(l => /\t/.test(l));
  const hasSemi = lines.some(l => /;/.test(l));

  // Probe for a header (text-only first line).
  let probableHasHeader = false;
  if (lines.length > 1) {
    const first = lines[0];
    // If the first line has letters and the rest don't (or are mostly numeric),
    // assume header.
    if (/[A-Za-z]/.test(first) && !/^-?\d/.test(first.replace(/[\s,;\t]/g, ""))) {
      probableHasHeader = true;
    }
  }

  // Case A — single-column european numbers (no tab, no ';')
  if (!hasTab && !hasSemi) {
    const startIdx = probableHasHeader ? 1 : 0;
    const dataSlice = lines.slice(startIdx);
    if (dataSlice.length > 0 && dataSlice.every(l => SINGLE_NUM_RE.test(l))) {
      lines = (probableHasHeader ? [lines[0]] : []).concat(
        dataSlice.map(normalizeEU)
      );
    }
  }

  // Detect delimiter by scanning first 3 lines
  const sample = lines.slice(0, 3).join("\n");
  const delim = /\t/.test(sample) ? "\t" : /[,;]/.test(sample) ? (/;/.test(sample) ? ";" : ",") : null;

  let headers = null;
  let dataLines = lines;
  if (delim) {
    const first = lines[0].split(delim).map(s => s.trim());
    const nonNumericCount = first.filter(s => isNaN(parseFloat(s))).length;
    if (nonNumericCount >= Math.ceil(first.length / 2)) {
      headers = first.map(h => h.toLowerCase());
      dataLines = lines.slice(1);
    }
  }

  // Column detection
  const pnlAliases = ["pnl", "p&l", "pl", "profit", "net", "daily_pnl", "dailypnl",
                       "p/l", "net p/l", "netpnl", "net pnl", "realized p/l", "realized pnl",
                       "profit/loss", "pnl ($)", "beneficio", "ganancia"];
  const maeAliases = ["mae", "adverse", "max_adverse", "drawdown_intra", "maxdd"];
  const mfeAliases = ["mfe", "favorable", "max_favorable"];
  // Fase 15-16 · Bloque 8a — date aliases for trade-level detection
  const dateAliases = ["date", "time", "datetime", "day", "fecha", "exit time", "exittime",
                        "close time", "closetime", "boughttimestamp", "soldtimestamp", "entry time"];
  let pnlIdx = 0, maeIdx = -1, mfeIdx = -1, dateIdx = -1;
  if (headers) {
    const idxOf = (aliases) =>
      headers.findIndex(h => aliases.some(a => h === a || h.includes(a)));
    const found = idxOf(pnlAliases);
    pnlIdx = found >= 0 ? found : 0;
    maeIdx = idxOf(maeAliases);
    mfeIdx = idxOf(mfeAliases);
    dateIdx = idxOf(dateAliases);
  }

  // Case B — normalise european numbers inside each token (only when delimiter
  // is tab or ';', because comma-delimited files can't have decimal commas).
  const normaliseTok = (tok) => (
    (delim === "\t" || delim === ";") && EU_NUM_RE.test(tok)
      ? normalizeEU(tok)
      : tok
  );

  // Fase 15-16 · Bloque 8a — accounting-bracket & currency cleanup
  // Turn "(123.45)" into "-123.45", strip "$ € " and trailing spaces.
  const cleanMoney = (tok) => {
    if (!tok) return tok;
    let t = tok.replace(/[$€£\s]/g, "");
    const m = t.match(/^\(([\d.,]+)\)$/);
    if (m) t = "-" + m[1];
    return t;
  };

  // Trade-level mode detection: header has both a date column and a pnl column.
  const tradeLevelMode = headers && dateIdx >= 0 && pnlIdx >= 0;

  // Date parser — accepts YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, with optional time.
  // Returns the date portion as `YYYY-MM-DD` (for grouping) or null.
  function detectDateFormat(samples) {
    // If any sample's first part is >12, it's DD/MM. Otherwise default MM/DD.
    for (const s of samples) {
      const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (m && parseInt(m[1], 10) > 12) return "DD/MM";
    }
    return "MM/DD";
  }
  function parseDate(tok, fmt) {
    if (!tok) return null;
    const clean = tok.trim().split(/[ T]/)[0]; // strip time
    // ISO
    let m = clean.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
    m = clean.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (m) {
      let yyyy = m[3].length === 2 ? `20${m[3]}` : m[3];
      const a = m[1].padStart(2, "0"), b = m[2].padStart(2, "0");
      return fmt === "DD/MM" ? `${yyyy}-${b}-${a}` : `${yyyy}-${a}-${b}`;
    }
    return null;
  }

  const data = [];
  const errors = [];
  let dateFmt = "MM/DD";
  let dateFmtNote = false;
  let nTrades = 0;
  if (tradeLevelMode) {
    // Detect date format from first ~50 rows
    const probe = dataLines.slice(0, 50).map(l => {
      const parts = l.split(delim).map(s => s.trim());
      return parts[dateIdx] || "";
    });
    dateFmt = detectDateFormat(probe);
    // Whether the heuristic stayed ambiguous (no day > 12 seen anywhere)
    dateFmtNote = !probe.some(s => {
      const m = s.match(/^(\d{1,2})\/(\d{1,2})/);
      return m && parseInt(m[1], 10) > 12;
    }) && probe.some(s => /\//.test(s));

    const dayMap = new Map(); // date → { pnl, count, mae?, mfe? }
    for (const line of dataLines) {
      const parts = line.split(delim).map(s => s.trim());
      const dateRaw = parts[dateIdx];
      const date = parseDate(dateRaw, dateFmt);
      const pnl = parseFloat(normaliseTok(cleanMoney(parts[pnlIdx])));
      if (date && isFinite(pnl)) {
        const entry = dayMap.get(date) || { pnl: 0, count: 0 };
        entry.pnl += pnl;
        entry.count += 1;
        // Capture mae/mfe ONLY on the first row of the date. If a later row
        // for the same date arrives we'll know it's multi-trade-per-day and
        // discard the partial MAE/MFE below (it isn't aggregatable).
        if (entry.count === 1) {
          if (maeIdx >= 0 && parts[maeIdx] !== undefined) {
            const v = parseFloat(normaliseTok(cleanMoney(parts[maeIdx])));
            if (isFinite(v)) entry.mae = Math.abs(v);
          }
          if (mfeIdx >= 0 && parts[mfeIdx] !== undefined) {
            const v = parseFloat(normaliseTok(cleanMoney(parts[mfeIdx])));
            if (isFinite(v)) entry.mfe = v;
          }
        }
        dayMap.set(date, entry);
        nTrades++;
      }
    }
    // ─── Fase 17B extra · trade-level MAE/MFE preservation ─────────────
    // If nDays === nTrades, each date had exactly one row → this is a
    // DAILY history that happens to include a date column (e.g. the
    // built-in `◇ load example`). Keep mae/mfe so hasMae detection still
    // works downstream. If any date had > 1 row it's a real per-trade
    // export and MAE/MFE can't be aggregated meaningfully — drop them.
    const sortedDates = [...dayMap.keys()].sort();
    const isOneRowPerDay = nTrades === sortedDates.length;
    for (const d of sortedDates) {
      const e = dayMap.get(d);
      const row = { pnl: e.pnl };
      if (isOneRowPerDay) {
        if (e.mae !== undefined) row.mae = e.mae;
        if (e.mfe !== undefined) row.mfe = e.mfe;
      }
      data.push(row);
    }
  } else {
    for (const line of dataLines) {
      const parts = delim
        ? line.split(delim).map(s => normaliseTok(cleanMoney(s.trim())))
        : [normaliseTok(cleanMoney(line))];
      const pnl = parseFloat(parts[pnlIdx]);
      if (!isFinite(pnl)) continue;
      const row = { pnl };
      if (maeIdx >= 0 && parts[maeIdx] !== undefined) {
        const v = parseFloat(parts[maeIdx]);
        if (isFinite(v)) row.mae = Math.abs(v);
      }
      if (mfeIdx >= 0 && parts[mfeIdx] !== undefined) {
        const v = parseFloat(parts[mfeIdx]);
        if (isFinite(v)) row.mfe = v;
      }
      data.push(row);
    }
  }

  if (data.length < 30) errors.push("Minimum 30 samples recommended; got " + data.length);
  if (data.length < 100 && data.length >= 30) errors.push("For reliable bootstrap use >=100 samples; got " + data.length);
  if (tradeLevelMode && dateFmtNote) {
    errors.push(dateFmt === "MM/DD" ? "dates interpreted as MM/DD" : "dates interpreted as DD/MM");
  }

  const stats = computeStatsFromData(data);
  // Fase 17B extra · only flag as "trade-level" when there are actually more
  // trades than days. With exactly one row per date we have a plain daily
  // history with a date column — no notice should fire.
  if (tradeLevelMode && nTrades > data.length) {
    stats.tradeLevel = true;
    stats.nTrades = nTrades;
    stats.nDays = data.length;
  }

  return { data, errors, stats };
}

// ─── Stats from data (Fase 13 refactor) ───────────────────────────────────
// Pure function: takes an array of {pnl, mae?, mfe?} rows and returns the
// same stats shape produced by parseBootstrapData. Reused by computeParameterCI
// so each bootstrap replica gets its own sigmas/wr (critical for the MAE
// estimator coherence introduced in Phase 12 Fix 1a).
export function computeStatsFromData(data) {
  const wins = data.filter(d => d.pnl > 0);
  const losses = data.filter(d => d.pnl < 0);
  const total = data.reduce((a, d) => a + d.pnl, 0);
  const meanWin = wins.length ? wins.reduce((a, d) => a + d.pnl, 0) / wins.length : 0;
  const meanLoss = losses.length ? losses.reduce((a, d) => a + d.pnl, 0) / losses.length : 0;
  const best = data.reduce((a, d) => d.pnl > a ? d.pnl : a, -Infinity);
  const worst = data.reduce((a, d) => d.pnl < a ? d.pnl : a, Infinity);
  const hasMae = data.some(d => d.mae !== undefined);

  const std = (arr, mu) => arr.length >= 2
    ? Math.sqrt(arr.reduce((a, v) => a + (v - mu) ** 2, 0) / arr.length)
    : 0;
  const winPnls  = wins.map(d => d.pnl);
  const lossPnls = losses.map(d => d.pnl);
  const sigmaWin  = wins.length   >= 2 ? std(winPnls, meanWin)   : Math.max(Math.abs(meanWin)  * 0.5, 1);
  const sigmaLoss = losses.length >= 2 ? std(lossPnls, meanLoss) : Math.max(Math.abs(meanLoss) * 0.5, 1);

  const previewValues = data.slice(0, 5).map(d => d.pnl);

  let autocorr = 0;
  if (data.length > 5) {
    const xs = data.map(d => d.pnl);
    const mu = total / data.length;
    let num = 0, den = 0;
    for (let i = 0; i < xs.length - 1; i++) num += (xs[i] - mu) * (xs[i + 1] - mu);
    for (let i = 0; i < xs.length;     i++) den += (xs[i] - mu) * (xs[i] - mu);
    autocorr = den > 0 ? num / den : 0;
  }

  // Fase 14 · Bloque 3 — automatic block length for stationary bootstrap.
  // Heuristic: L = (1 + ac)/(1 - ac), clamped to [1, 10]. Below ac ≈ 0.05 the
  // gain from blocks is negligible; we force L = 1 (pure IID) so behaviour is
  // unchanged for traders whose days are independent.
  const blockLen = autocorr > 0.05
    ? Math.min(10, Math.max(1, Math.round((1 + autocorr) / (1 - autocorr))))
    : 1;

  return {
    total: data.length,
    wins: wins.length,
    losses: losses.length,
    wr: data.length ? wins.length / data.length : 0,
    meanWin, meanLoss,
    totalPnl: total,
    best, worst,
    hasMae,
    sigmaWin,
    sigmaLoss,
    previewValues,
    autocorrelation: +autocorr.toFixed(3),
    blockLen,
  };
}

// ─── Parameter CI via double bootstrap (Fase 13) ──────────────────────────
// Resamples the user's dataset B times, recomputes the full Monte Carlo for
// each replica (including a fresh `bootstrapStats` so MAE estimation stays
// coherent), and returns the empirical 5/50/95 quantiles of P(PASS).
export function computeParameterCI(account, strategy, opts = {}) {
  const B = opts.replicas ?? 20;
  const simsPer = opts.simsPerReplica ?? 1500;
  const data = strategy.bootstrapData;
  if (!data || data.length < 10 || strategy.mode !== "bootstrap") return null;

  // Seed master: each replica gets `seed + b` so the whole CI is reproducible.
  // Note: replicas resample IID — they estimate *parameter* uncertainty, which
  // is conceptually independent of the trajectory-clustering question handled
  // by block bootstrap (Fase 14 · Bloque 3) inside each run.
  const baseSeed = Number.isFinite(opts.seed)
    ? (opts.seed >>> 0)
    : ((Date.now() ^ (Math.random() * 1e9)) >>> 0);
  _rand = mulberry32(baseSeed); // for the resample step

  const ps = [];
  for (let b = 0; b < B; b++) {
    const resampled = Array.from({ length: data.length },
      () => data[Math.floor(_rand() * data.length)]);
    const stats = computeStatsFromData(resampled);
    const sres = {
      ...strategy,
      bootstrapData: resampled,
      bootstrapStats: stats,
      postPassEnabled: false, // disable funded cycle for speed inside replicas
    };
    const r = runMonteCarlo(account, sres, simsPer, null, { seed: baseSeed + b + 1 });
    ps.push(r.pPass);
    // Restore _rand for the next resample (runMonteCarlo replaced it).
    _rand = mulberry32(baseSeed + 0x9E3779B9 * (b + 1));
  }
  ps.sort((a, b2) => a - b2);
  const q = (p) => ps[Math.min(Math.floor(ps.length * p), ps.length - 1)];
  return {
    p5: q(0.05),
    p50: q(0.5),
    p95: q(0.95),
    width: q(0.95) - q(0.05),
    replicas: B,
    simsPerReplica: simsPer,
    nData: data.length,
    seed: baseSeed,
  };
}

// ─── Stress test (Fase 13) ─────────────────────────────────────────────────
// Sweeps (winErosion pp × lossAmplify %) and reports EV/pPass per cell plus
// the linear-interpolated degradation margin where EV crosses zero.
function crossZero(points) {
  // points: array of [x, ev] sorted (we sort defensively)
  const pts = points.slice().sort((a, b) => a[0] - b[0]);
  if (!pts.length) return null;
  if (pts[0][1] <= 0) return 0;
  for (let i = 1; i < pts.length; i++) {
    const [x0, e0] = pts[i - 1];
    const [x1, e1] = pts[i];
    if (e1 <= 0) {
      // Linear interpolation of the EV=0 crossing between x0 and x1.
      const t = e0 / (e0 - e1);
      return x0 + t * (x1 - x0);
    }
  }
  return Infinity;
}

export function runStressTest(account, strategy, opts = {}) {
  const nSims = opts.simsPerCell ?? 3000;
  const rows = opts.rows ?? [0, 2.5, 5, 7.5];      // WR erosion in pp
  const cols = opts.cols ?? [0, 0.10, 0.20, 0.30]; // Loss amplification

  const baseSeed = Number.isFinite(opts.seed)
    ? (opts.seed >>> 0)
    : ((Date.now() ^ (Math.random() * 1e9)) >>> 0);

  const wrBase = strategy.mode === "bootstrap"
    ? (strategy.bootstrapStats?.wr ?? 0)
    : (strategy.wr ?? 0);
  const lossPool = strategy.mode === "bootstrap"
    ? (strategy.bootstrapData || []).filter(d => d.pnl < 0)
    : null;

  const grid = [];
  let cellSeedOffset = 0;
  for (const pp of rows) {
    for (const lam of cols) {
      cellSeedOffset++;
      const q = pp > 0 ? (pp / 100) / Math.max(wrBase, 0.01) : 0;
      if (q > 1 || (pp > 0 && lossPool && lossPool.length === 0)) {
        grid.push({ pp, lam, na: true });
        continue;
      }
      const s = {
        ...strategy,
        stress: { winErosionQ: q, lossAmplify: lam },
        _lossPool: lossPool,
        postPassEnabled: false,
      };
      const r = runMonteCarlo(account, s, nSims, null, { seed: baseSeed + cellSeedOffset });
      grid.push({ pp, lam, pPass: r.pPass, ev: r.ev });
    }
  }

  // Margins: where EV crosses zero along the base row / base column.
  const base = grid.filter(g => g.lam === 0 && !g.na).map(g => [g.pp, g.ev]);
  const col0 = grid.filter(g => g.pp === 0 && !g.na).map(g => [g.lam * 100, g.ev]);
  const marginWRpp   = crossZero(base);
  const marginLossPct = crossZero(col0);

  return { grid, rows, cols, marginWRpp, marginLossPct, simsPerCell: nSims, seed: baseSeed };
}

// ─── Sizing sweep (Fase 14 · Bloque 5) ────────────────────────────────────
// Runs the same strategy at multiple size multipliers under the same seed
// (Common Random Numbers — the only differences come from the multiplier).
// Returns the (k, pPass, ev, medianPass, br95) curve plus the EV/pPass optima.
export function runSizingSweep(account, strategy, opts = {}) {
  const ks    = opts.ks       ?? [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
  const nSims = opts.simsPerK ?? 4000;
  const seed  = Number.isFinite(opts.seed) ? (opts.seed >>> 0) : 777;
  const points = ks.map((k) => {
    const r = runMonteCarlo(
      account,
      { ...strategy, sizeMultiplier: k, postPassEnabled: false },
      nSims, null,
      { seed }, // SAME seed across ks → CRN, the curve has no jitter
    );
    return { k, pPass: r.pPass, ev: r.ev, medianPass: r.medianPass, br95: r.br95 };
  });
  const kBestEV   = points.reduce((a, b) => b.ev    > a.ev    ? b : a).k;
  const kBestPass = points.reduce((a, b) => b.pPass > a.pPass ? b : a).k;
  return { points, kBestEV, kBestPass, seed, simsPerK: nSims };
}

// ─── Strategy analysis (Fase 14 · Bloque 4) ───────────────────────────────
// Standalone, firm-agnostic statistical view of the user's track record.
// Pure JS, no Monte Carlo internally beyond cheap re-sampled trajectories
// (~2000 per horizon × 3 horizons = 6000 lightweight loops, <250 ms typical).

// Normal CDF via Abramowitz–Stegun approximation of erf.
function normCdf(x) {
  // erf approximation 7.1.26 (max error ~1.5e-7)
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * ax);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-ax * ax);
  return 0.5 * (1 + sign * y);
}

function quantileSorted(sorted, p) {
  if (!sorted.length) return 0;
  return sorted[Math.min(Math.floor(sorted.length * p), sorted.length - 1)];
}

// One re-sampled equity trajectory using the same block-bootstrap sampler as
// the main engine. Returns { maxDD, worstStreak, daysUnderwater }.
function simulateTrajectory(strategy, horizonDays) {
  const sampler = { idx: -1 };
  let equity = 0;
  let peak = 0;
  let maxDD = 0;
  let curLossStreak = 0;
  let worstStreak = 0;
  let daysUnder = 0;
  for (let d = 0; d < horizonDays; d++) {
    const { pnl } = genDay(strategy, 1.0, sampler);
    equity += pnl;
    if (equity > peak) peak = equity;
    const dd = peak - equity;
    if (dd > maxDD) maxDD = dd;
    if (equity < peak) daysUnder++;
    if (pnl < 0) {
      curLossStreak++;
      if (curLossStreak > worstStreak) worstStreak = curLossStreak;
    } else {
      curLossStreak = 0;
    }
  }
  return { maxDD, worstStreak, daysUnderwater: daysUnder };
}

export function analyzeStrategy(strategy, opts = {}) {
  const data = strategy.bootstrapData;
  const st   = strategy.bootstrapStats;
  if (!data || data.length < 30 || !st) return null;

  const seed = Number.isFinite(opts.seed) ? (opts.seed >>> 0) : 12345;
  _rand = mulberry32(seed);

  const n = data.length;
  const pnls = data.map(d => d.pnl);

  // Edge stats
  const mu  = pnls.reduce((a, b) => a + b, 0) / n;
  const sd  = Math.sqrt(pnls.reduce((a, v) => a + (v - mu) ** 2, 0) / Math.max(1, n - 1));
  const tStat  = sd > 0 ? mu / (sd / Math.sqrt(n)) : 0;
  const pValue = 2 * (1 - normCdf(Math.abs(tStat)));
  const sqn    = sd > 0 ? (mu / sd) * Math.sqrt(Math.min(n, 100)) : 0;

  const grossWin  = pnls.filter(v => v > 0).reduce((a, b) => a + b, 0);
  const grossLoss = Math.abs(pnls.filter(v => v < 0).reduce((a, b) => a + b, 0));
  const profitFactor = grossLoss > 0 ? grossWin / grossLoss : Infinity;

  // Kelly: f* = (wr · b − (1 − wr)) / b, with b = |meanWin/meanLoss|.
  const b = (st.meanWin && st.meanLoss) ? Math.abs(st.meanWin / st.meanLoss) : 0;
  const kelly = b > 0 ? (st.wr * b - (1 - st.wr)) / b : 0;

  // Distributions via re-sampled trajectories at 3 horizons
  const nRuns = opts.runsPerHorizon ?? 2000;
  function distAt(h) {
    const dds = [], streaks = [], under = [];
    for (let i = 0; i < nRuns; i++) {
      const tr = simulateTrajectory(strategy, h);
      dds.push(tr.maxDD); streaks.push(tr.worstStreak); under.push(tr.daysUnderwater / h);
    }
    dds.sort((a, b2) => a - b2);
    streaks.sort((a, b2) => a - b2);
    return {
      maxDD: { p50: quantileSorted(dds, 0.5), p90: quantileSorted(dds, 0.9) },
      streak: { p50: quantileSorted(streaks, 0.5), p90: quantileSorted(streaks, 0.9) },
      meanUnderwater: under.reduce((a, b2) => a + b2, 0) / under.length,
    };
  }
  const h30 = distAt(30);
  const h60 = distAt(60);
  const h90 = distAt(90);

  // Split-half robustness check
  const halfA = pnls.slice(0, Math.floor(n / 2));
  const halfB = pnls.slice(Math.floor(n / 2));
  const meanA = halfA.reduce((a, b2) => a + b2, 0) / halfA.length;
  const meanB = halfB.reduce((a, b2) => a + b2, 0) / halfB.length;
  const wrA = halfA.filter(v => v > 0).length / halfA.length;
  const wrB = halfB.filter(v => v > 0).length / halfB.length;
  const sdA = Math.sqrt(halfA.reduce((a, v) => a + (v - meanA) ** 2, 0) / Math.max(1, halfA.length - 1));
  const sdB = Math.sqrt(halfB.reduce((a, v) => a + (v - meanB) ** 2, 0) / Math.max(1, halfB.length - 1));
  const seComb = Math.sqrt((sdA * sdA) / halfA.length + (sdB * sdB) / halfB.length);
  const splitFlag = seComb > 0 ? Math.abs(meanA - meanB) / seComb > 2 : false;

  // Concentration check — strip the 3 best days
  const sortedDesc = [...pnls].sort((a, b2) => b2 - a);
  const top3 = sortedDesc.slice(0, 3).reduce((a, b2) => a + b2, 0);
  const totalPnl = pnls.reduce((a, b2) => a + b2, 0);
  const withoutTop3 = totalPnl - top3;
  const concentrationFlag = withoutTop3 <= 0;

  return {
    n,
    expectancyDay: mu,
    sd,
    tStat,
    pValue,
    sqn,
    profitFactor,
    kelly,
    maxDD: { d30: h30.maxDD, d60: h60.maxDD, d90: h90.maxDD },
    worstStreak: h30.streak,
    timeUnderwaterPct: h90.meanUnderwater,
    splitHalf: { wrA, wrB, meanA, meanB, flag: splitFlag },
    concentration: { totalPnl, withoutTop3, flag: concentrationFlag },
    seed,
  };
}


// ═══════════════════════════════════════════════════════════════════════════
// Business simulator (Sep 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Simulates running several prop-firm accounts as ONE business over a horizon:
// a limited bankroll buys evaluations, passed evals become funded accounts,
// payouts are split between reinvestment (cash that buys new evals) and the
// trader's pocket, breached accounts are replaced or reset, and funded caps
// are shared per firm group. All accounts trade the same strategy, so by
// default they draw the SAME day (copy-trading → perfectly correlated), which
// is what makes a business fragile and is the effect a per-account sim misses.
//
// items[] (pre-resolved on the main thread so the engine stays DB-free):
//   { key, label, group, plan, fundedRules, fee, activation, monthly, sizeMult }
// config:
//   bankroll, horizonDays, reinvestPct (0..1), parallelAt ($ net worth at which
//   every funded account trades daily; below it only `startSlots` of them trade
//   each day, taking turns), cushion
//   ($ never spent on evals), dayCap ($ max profit per account per day, 0 = off),
//   correlation ("copy" | "independent"), groupCaps { group: n }, useResets,
//   edgeHaircut (0..1), sampleEvery (days between trajectory points), lite.

function bizResolveStrategy(strategy) {
  const s = { ...strategy, postPassEnabled: false };
  if (strategy.commissionMode === "estimate") {
    s.dailyCommission = (strategy.commissionPerRT || 0) * (strategy.tradesPerDay || 0) *
                        (strategy.contractsPerTrade || 1);
  } else if (strategy.commissionMode === "fixed") {
    s.dailyCommission = strategy.dailyCommission || 0;
  } else {
    s.dailyCommission = 0;
  }
  return s;
}

function bizMeanDailyPnl(strategy) {
  if (strategy.mode === "bootstrap" && strategy.bootstrapData?.length) {
    return mean(strategy.bootstrapData.map(d => d.pnl));
  }
  const wr = strategy.wr ?? 0;
  return wr * (strategy.muWin || 0) + (1 - wr) * (strategy.muLoss || 0);
}

// One raw day at size 1 (shared across copy-traded accounts).
function bizDrawDay(S, sampler, sigWin) {
  const { pnl, mae } = genDay(S, 1.0, sampler);
  const mfe = pnl > 0
    ? pnl + gammaRandom(1.5, Math.max(sigWin * 0.15, 1))
    : gammaRandom(1.2, Math.max(sigWin * 0.35, 1));
  return { pnl, mae, mfe };
}

function bizNewEval(item) {
  const p = item.plan;
  return {
    item, phase: 1,
    rules: { ...p },
    equity: p.capital, hwm: p.capital, floor: p.capital - p.ddValue, floorLocked: false,
    totalProfit: 0, bestDay: 0, days: 0, winningDays: 0, sizeFactor: 1.0,
    sampler: { idx: -1 },
  };
}

function bizStartPhase2(a) {
  const p = a.item.plan;
  a.phase = 2;
  a.rules = { ...p, target: p.phase2.target, minDays: p.phase2.minDays ?? 0, maxDays: p.phase2.maxDays ?? null };
  a.equity = p.capital; a.hwm = p.capital; a.floor = p.capital - p.ddValue; a.floorLocked = false;
  a.totalProfit = 0; a.bestDay = 0; a.days = 0; a.winningDays = 0; a.sizeFactor = 1.0;
}

// Mirrors simulateSinglePhase for ONE day. Returns "PASS" | "FAIL" | null.
function bizStepEval(a, day, k, dayCap, beh) {
  const acc = a.rules;
  const cap0 = a.item.plan.capital;
  a.days++;
  const activeDays = beh.maxDaysType === "calendar" ? Math.ceil(a.days * 1.4) : a.days;
  if (acc.maxDays && activeDays > acc.maxDays) return "FAIL";

  const m = k * a.sizeFactor;
  let pnl = day.pnl * m;
  const mae = day.mae * m;
  if (dayCap > 0 && pnl > dayCap) pnl = dayCap;

  if (acc.ddType === "trailing_intraday" && !a.floorLocked) {
    const peak = a.equity + day.mfe * m;
    if (peak > a.hwm) {
      a.hwm = peak;
      a.floor = Math.max(a.floor, computeFloor(acc, a.hwm, a.equity, cap0));
    }
  }
  if (a.equity - mae <= a.floor) return "FAIL";
  if (acc.dailyLoss && pnl <= -acc.dailyLoss) {
    if (acc.dailyLossIsFatal) return "FAIL";
    pnl = -acc.dailyLoss;
  }
  a.equity += pnl;
  if (a.equity <= a.floor) return "FAIL";
  if (acc.ddType !== "static" && !a.floorLocked) {
    if (a.equity > a.hwm) a.hwm = a.equity;
    if (acc.floorLock === "at_capital_plus_100" && a.equity >= cap0 + acc.ddValue + 100) {
      a.floorLocked = true;
      a.floor = cap0 + 100;
    } else {
      a.floor = Math.max(a.floor, computeFloor(acc, a.hwm, a.equity, cap0));
    }
  }
  if (pnl > 0) {
    a.totalProfit += pnl;
    if (pnl > a.bestDay) a.bestDay = pnl;
  }
  if (pnl >= beh.winDayThreshold) a.winningDays++;

  let effectiveTarget = acc.target;
  let consistencyOK = true;
  if (acc.consistency && acc.consistencyType === "vs_target" && a.bestDay > 0) {
    effectiveTarget = Math.max(acc.target, a.bestDay / acc.consistency);
  } else if (acc.consistency && acc.consistencyType === "vs_total" && a.totalProfit > 0) {
    consistencyOK = a.bestDay <= acc.consistency * a.totalProfit;
  }
  const minDaysMet = beh.minDaysType === "winning"
    ? a.winningDays >= (acc.minDays || 0)
    : a.days >= (acc.minDays || 0);
  const reached = a.equity - cap0 >= effectiveTarget;
  if (reached && minDaysMet && consistencyOK) return "PASS";
  if (reached && !consistencyOK && beh.postTargetMode === "conservative") {
    a.sizeFactor = beh.postTargetSizeReduction;
  } else if (!reached && a.sizeFactor < 1.0 && beh.postTargetMode === "conservative") {
    a.sizeFactor = 1.0;
  }
  return null;
}

function bizNewFunded(item, resetsLeft) {
  const r = item.fundedRules;
  const cap0 = item.plan.capital;
  return {
    item, resetsLeft,
    equity: cap0, hwm: cap0, floor: cap0 - r.ddValue, floorLocked: false,
    days: 0, daysSince: 0, winDaysSince: 0, bestDaySince: 0, profitSince: 0,
    equityAtPayout: cap0, nPayouts: 0,
    acct: { ddType: r.ddType, ddValue: r.ddValue, floorLock: r.floorLock, target: 0 },
    sampler: { idx: -1 },
  };
}

// One funded day. Returns { breach } | { payout, graduated } | null.
function bizStepFunded(f, day, k, dayCap) {
  const r = f.item.fundedRules;
  const cap0 = f.item.plan.capital;
  f.days++; f.daysSince++;
  let pnl = day.pnl * k;
  const mae = day.mae * k;
  if (dayCap > 0 && pnl > dayCap) pnl = dayCap;

  if (r.ddType === "trailing_intraday" && !f.floorLocked) {
    const peak = f.equity + day.mfe * k;
    if (peak > f.hwm) {
      f.hwm = peak;
      f.floor = Math.max(f.floor, computeFloor(f.acct, f.hwm, f.equity, cap0));
    }
  }
  if (f.equity - mae <= f.floor) return { breach: true };
  if (r.dailyLoss && pnl <= -r.dailyLoss) {
    if (r.dailyLossIsFatal) return { breach: true };
    pnl = -r.dailyLoss;
  }
  f.equity += pnl;
  if (f.equity <= f.floor) return { breach: true };
  if (r.ddType !== "static" && !f.floorLocked) {
    if (f.equity > f.hwm) f.hwm = f.equity;
    if (r.floorLock === "at_capital_plus_100" && f.equity >= cap0 + r.ddValue + 100) {
      f.floorLocked = true;
      f.floor = cap0 + 100;
    } else {
      f.floor = Math.max(f.floor, computeFloor(f.acct, f.hwm, f.equity, cap0));
    }
  }
  if (pnl > 0) {
    f.profitSince += pnl;
    if (pnl > f.bestDaySince) f.bestDaySince = pnl;
    if (r.payoutWinDays > 0 && pnl >= (r.payoutWinDayMin || 0)) f.winDaysSince++;
  }
  const d = payoutDecision({
    equity: f.equity, nPayouts: f.nPayouts, daysSince: f.daysSince,
    winDaysSince: f.winDaysSince, bestDaySince: f.bestDaySince,
    profitSince: f.profitSince, netSince: f.equity - f.equityAtPayout,
  }, r, cap0, f.item.plan.profitSplit);
  if (!d) return null;

  f.equity -= d.gross;
  if (r.resetOnPayout && r.resetFloorOnPayout !== false) {
    f.hwm = cap0; f.floor = cap0 - r.ddValue; f.floorLocked = false;
  }
  f.nPayouts++;
  f.daysSince = 0; f.winDaysSince = 0; f.bestDaySince = 0; f.profitSince = 0;
  f.equityAtPayout = f.equity;
  return { payout: d, graduated: !!(r.maxPayouts && f.nPayouts >= r.maxPayouts) };
}

function bizOneRun(items, S, cfg, beh, sigWin) {
  const H = cfg.horizonDays;
  const nItems = items.length;
  const reinvest = cfg.reinvestPct;
  const copy = cfg.correlation !== "independent";

  let cash = cfg.bankroll;
  let pocket = 0;
  let evals = [];
  let funded = [];
  let pending = []; // passed evals waiting for cash to pay the activation fee
  let scale = !(cfg.parallelAt > 0);
  let rot = 0, buyIdx = 0;
  let dead = false, deadDay = null;
  let firstPayoutDay = null, nPayouts = 0, payoutNet = 0, fees = 0, evalsBought = 0;
  let peakFunded = 0, minCash = cash;
  let scaleDay = scale ? 0 : null;
  const groupCount = {};
  const per = items.map(() => ({ bought: 0, passed: 0, failed: 0, breaches: 0, resets: 0,
                                 payouts: 0, payoutNet: 0, graduated: 0 }));
  const idxOf = new Map(items.map((it, i) => [it, i]));
  const traj = cfg.lite ? null : [];
  const sharedSampler = { idx: -1 };
  const capOf = (g) => cfg.groupCaps?.[g] ?? 5;

  for (let day = 1; day <= H; day++) {
    if (!scale && cash + pocket >= cfg.parallelAt) { scale = true; scaleDay = day; }

    // ── 0) Activate passed evals as soon as cash allows (max 30 days) ──
    if (pending.length) {
      const still = [];
      for (const pa of pending) {
        const it = pa.item;
        if (cash >= it.activation) {
          cash -= it.activation; fees += it.activation;
          per[idxOf.get(it)].passed++;
          funded.push(bizNewFunded(it, cfg.useResets ? (it.fundedRules.fundedResets || 0) : 0));
        } else if (day - pa.since > 30) {
          per[idxOf.get(it)].failed++; groupCount[it.group]--;
        } else {
          still.push(pa);
        }
      }
      pending = still;
    }
    const reserved = pending.reduce((acc, pa) => acc + pa.item.activation, 0);

    // ── 1) Buy evals: round-robin over the mix while cash and caps allow ──
    let bought = true;
    while (bought) {
      bought = false;
      for (let j = 0; j < nItems; j++) {
        const it = items[(buyIdx + j) % nItems];
        if ((groupCount[it.group] || 0) >= capOf(it.group)) continue;
        if (cash - reserved - it.fee < cfg.cushion) continue;
        cash -= it.fee; fees += it.fee; evalsBought++;
        per[idxOf.get(it)].bought++;
        groupCount[it.group] = (groupCount[it.group] || 0) + 1;
        evals.push(bizNewEval(it));
        buyIdx = (buyIdx + j + 1) % nItems;
        bought = true;
        break;
      }
    }
    // Nothing alive and nothing affordable → the business is dead.
    if (!evals.length && !funded.length && !pending.length) {
      dead = true; deadDay = day;
      if (traj) {
        for (let d2 = day; d2 <= H; d2++) if (d2 % cfg.sampleEvery === 0) traj.push(cash + pocket);
      }
      break;
    }

    const shared = copy ? bizDrawDay(S, sharedSampler, sigWin) : null;

    // ── 2) Evaluations ──
    const keepEvals = [];
    for (const a of evals) {
      const it = a.item;
      const pi = idxOf.get(it);
      // Monthly plans renew every 21 trading days; unaffordable → abandoned.
      if (it.monthly && a.days > 0 && a.days % 21 === 0) {
        if (cash >= it.fee) { cash -= it.fee; fees += it.fee; }
        else { per[pi].failed++; groupCount[it.group]--; continue; }
      }
      const dayA = shared || bizDrawDay(S, a.sampler, sigWin);
      const res = bizStepEval(a, dayA, it.sizeMult, cfg.dayCap, beh);
      if (res === "FAIL") { per[pi].failed++; groupCount[it.group]--; continue; }
      if (res === "PASS") {
        if (it.plan.phases === 2 && a.phase === 1) { bizStartPhase2(a); keepEvals.push(a); continue; }
        if (cash >= it.activation) {
          cash -= it.activation; fees += it.activation;
          per[pi].passed++;
          funded.push(bizNewFunded(it, cfg.useResets ? (it.fundedRules.fundedResets || 0) : 0));
        } else {
          pending.push({ item: it, since: day }); // activate once payouts bring cash in
        }
        continue;
      }
      keepEvals.push(a);
    }
    evals = keepEvals;

    // ── 3) Funded accounts: all trade once scaled; before that only
    //       `startSlots` trade each day, taking turns ──
    let tradeSet = funded;
    const slots = Math.max(1, cfg.startSlots || 1);
    if (!scale && funded.length > slots) {
      tradeSet = [];
      for (let j = 0; j < slots; j++) tradeSet.push(funded[(rot + j) % funded.length]);
      rot = (rot + slots) % funded.length;
    }
    const removed = new Set();
    for (const f of tradeSet) {
      const it = f.item;
      const pi = idxOf.get(it);
      const dayF = shared || bizDrawDay(S, f.sampler, sigWin);
      const res = bizStepFunded(f, dayF, it.sizeMult, cfg.dayCap);
      if (!res) continue;
      if (res.breach) {
        per[pi].breaches++;
        const rf = it.fundedRules.fundedResetFee || 0;
        if (f.resetsLeft > 0 && rf > 0 && cash - rf >= cfg.cushion) {
          cash -= rf; fees += rf; per[pi].resets++;
          Object.assign(f, bizNewFunded(it, f.resetsLeft - 1));
        } else {
          removed.add(f); groupCount[it.group]--;
        }
        continue;
      }
      const net = res.payout.net;
      nPayouts++; payoutNet += net;
      per[pi].payouts++; per[pi].payoutNet += net;
      if (firstPayoutDay === null) firstPayoutDay = day;
      cash += net * reinvest;
      pocket += net * (1 - reinvest);
      if (res.graduated) { per[pi].graduated++; removed.add(f); groupCount[it.group]--; }
    }
    if (removed.size) funded = funded.filter(f => !removed.has(f));

    if (funded.length > peakFunded) peakFunded = funded.length;
    if (cash < minCash) minCash = cash;
    if (traj && day % cfg.sampleEvery === 0) traj.push(cash + pocket);
  }

  return {
    profit: cash + pocket - cfg.bankroll, cash, pocket, dead, deadDay,
    firstPayoutDay, nPayouts, payoutNet, fees, evalsBought, peakFunded, minCash,
    scaleDay, per, traj,
  };
}

export function runBusinessSim(items, strategy, config, nSims = 1000, opts = {}) {
  if (!Array.isArray(items) || !items.length) throw new Error("business: empty mix");
  const cfg = {
    bankroll: 1500, horizonDays: 252, reinvestPct: 0.8, parallelAt: 4000, startSlots: 3, cushion: 0,
    dayCap: 0, correlation: "copy", groupCaps: {}, useResets: true, edgeHaircut: 0,
    sampleEvery: 5, lite: false,
    ...config,
  };
  const N = Math.min(10000, Math.max(50, Math.floor(nSims || 1000)));
  const seed = Number.isFinite(opts.seed)
    ? (opts.seed >>> 0)
    : ((Date.now() ^ (Math.random() * 1e9)) >>> 0);
  _rand = mulberry32(seed);

  const S = bizResolveStrategy(strategy);
  if (cfg.edgeHaircut > 0) {
    const mu = bizMeanDailyPnl(strategy);
    if (mu > 0) S._edgeShift = cfg.edgeHaircut * mu;
  }
  const bs = S.mode === "bootstrap" ? S.bootstrapStats : null;
  const sigWin = (bs && bs.sigmaWin) ? bs.sigmaWin : (S.sigmaWin || 1);
  const beh = {
    postTargetMode:          S.postTargetMode || "conservative",
    postTargetSizeReduction: S.postTargetSizeReduction ?? 0.30,
    minDaysType:             S.minDaysType || "total",
    winDayThreshold:         S.winDayThreshold ?? 50,
    maxDaysType:             S.maxDaysType || "trading",
  };

  const runs = [];
  for (let i = 0; i < N; i++) runs.push(bizOneRun(items, S, cfg, beh, sigWin));

  const sortedBy = (key) => runs.map(r => r[key]).sort((a, b) => a - b);
  const profits = sortedBy("profit");
  const pRuin = runs.filter(r => r.dead).length / N;
  const withPayout = runs.filter(r => r.firstPayoutDay !== null)
    .map(r => r.firstPayoutDay).sort((a, b) => a - b);
  const scaleDays = runs.filter(r => r.scaleDay !== null && r.scaleDay > 0)
    .map(r => r.scaleDay).sort((a, b) => a - b);

  const summary = {
    nSims: N, seed, horizonDays: cfg.horizonDays, bankroll: cfg.bankroll,
    pRuin, pRuinCI: 1.96 * Math.sqrt(Math.max(pRuin * (1 - pRuin), 1e-9) / N),
    pLoss: runs.filter(r => r.profit < 0).length / N,
    profit: { mean: mean(profits), p10: quantile(profits, 0.10), p25: quantile(profits, 0.25),
              p50: quantile(profits, 0.5), p75: quantile(profits, 0.75), p90: quantile(profits, 0.90) },
    pocketMedian: quantile(sortedBy("pocket"), 0.5),
    pNoPayout: 1 - withPayout.length / N,
    firstPayoutMedian: withPayout.length ? quantile(withPayout, 0.5) : null,
    payoutsMedian: quantile(sortedBy("nPayouts"), 0.5),
    evalsMedian: quantile(sortedBy("evalsBought"), 0.5),
    peakFundedMedian: quantile(sortedBy("peakFunded"), 0.5),
    peakFundedP90: quantile(sortedBy("peakFunded"), 0.9),
    feesMedian: quantile(sortedBy("fees"), 0.5),
    minCashMedian: quantile(sortedBy("minCash"), 0.5),
    scaleDayMedian: scaleDays.length ? quantile(scaleDays, 0.5) : null,
    pScaled: scaleDays.length / N,
  };

  if (cfg.lite) return summary;

  // Per-item breakdown (means across runs)
  summary.perItem = items.map((it, i) => {
    const agg = { bought: 0, passed: 0, failed: 0, breaches: 0, resets: 0, payouts: 0, payoutNet: 0, graduated: 0 };
    for (const r of runs) for (const k of Object.keys(agg)) agg[k] += r.per[i][k];
    for (const k of Object.keys(agg)) agg[k] /= N;
    const resolved = agg.passed + agg.failed;
    return { key: it.key, label: it.label, group: it.group, ...agg,
             passRate: resolved > 0 ? agg.passed / resolved : null };
  });

  // Net-worth fan chart (cash + pocket, percentiles per sampled day)
  const nPts = Math.floor(cfg.horizonDays / cfg.sampleEvery);
  const fan = [];
  for (let j = 0; j < nPts; j++) {
    const col = runs.map(r => r.traj[j] ?? (r.cash + r.pocket)).sort((a, b) => a - b);
    const p10 = quantile(col, 0.10), p25 = quantile(col, 0.25), p50 = quantile(col, 0.5);
    const p75 = quantile(col, 0.75), p90 = quantile(col, 0.90);
    fan.push({ day: (j + 1) * cfg.sampleEvery, p10, p25, p50, p75, p90,
               band90: [p10, p90], band50: [p25, p75] });
  }
  summary.fan = fan;

  // Profit histogram (clipped to p2..p98 so one outlier doesn't flatten it)
  const lo = quantile(profits, 0.02), hi = quantile(profits, 0.98);
  const bins = 30;
  const w = Math.max(1, (hi - lo) / bins);
  const counts = Array.from({ length: bins }, () => 0);
  for (const v of profits) {
    if (v < lo || v > hi) continue;
    counts[Math.min(bins - 1, Math.floor((v - lo) / w))]++;
  }
  summary.hist = counts.map((c, i) => ({ x: Math.round(lo + (i + 0.5) * w), pct: +(100 * c / N).toFixed(2) }));
  return summary;
}
